/**
 * Reads a drag-and-drop payload into a flat list of files, remembering the folder
 * each one came from so a dropped folder can be recreated in the drive.
 *
 * Dropping a folder is only visible through the non-standard
 * `webkitGetAsEntry` API, which every current browser supports but TypeScript's
 * DOM types cover inconsistently, hence the minimal local interfaces below.
 */

export interface DroppedFile {
  file: File;
  /** Folder names between the drop target and the file, outermost first. */
  path: string[];
}

interface FsEntry {
  isFile: boolean;
  isDirectory: boolean;
  name: string;
}

interface FsFileEntry extends FsEntry {
  file: (onSuccess: (file: File) => void, onError: (err: unknown) => void) => void;
}

interface FsDirectoryEntry extends FsEntry {
  createReader: () => { readEntries: (onSuccess: (entries: FsEntry[]) => void, onError: (err: unknown) => void) => void };
}

function entryToFile(entry: FsFileEntry): Promise<File | null> {
  return new Promise((resolve) => {
    entry.file(
      (file) => resolve(file),
      () => resolve(null)
    );
  });
}

/** `readEntries` returns a page at a time, so it has to be called until it comes back empty. */
function readAllEntries(entry: FsDirectoryEntry): Promise<FsEntry[]> {
  const reader = entry.createReader();
  const all: FsEntry[] = [];
  return new Promise((resolve) => {
    const readNextBatch = () => {
      reader.readEntries(
        (entries) => {
          if (entries.length === 0) {
            resolve(all);
            return;
          }
          all.push(...entries);
          readNextBatch();
        },
        () => resolve(all)
      );
    };
    readNextBatch();
  });
}

async function walkEntry(entry: FsEntry, path: string[], out: DroppedFile[]): Promise<void> {
  if (entry.isFile) {
    const file = await entryToFile(entry as FsFileEntry);
    if (file) out.push({ file, path });
    return;
  }
  if (entry.isDirectory) {
    const children = await readAllEntries(entry as FsDirectoryEntry);
    for (const child of children) {
      await walkEntry(child, [...path, entry.name], out);
    }
  }
}

export async function readDroppedFiles(dataTransfer: DataTransfer): Promise<DroppedFile[]> {
  const items = Array.from(dataTransfer.items ?? []);
  const entries = items
    .map((item) => {
      const getEntry = (item as DataTransferItem & { webkitGetAsEntry?: () => FsEntry | null }).webkitGetAsEntry;
      return typeof getEntry === 'function' ? getEntry.call(item) : null;
    })
    .filter((entry): entry is FsEntry => entry != null);

  if (entries.length === 0) {
    // No entry API (or a payload that isn't files): fall back to a flat file list.
    return Array.from(dataTransfer.files ?? []).map((file) => ({ file, path: [] }));
  }

  const out: DroppedFile[] = [];
  for (const entry of entries) {
    await walkEntry(entry, [], out);
  }
  return out;
}
